import type {
  CandidatePatient,
  DeduplicationData,
  DeferredRecord,
  DuplicateCandidate,
  HistoryRecord
} from '@/types/deduplication'

// ─── Shared patients ────────────────────────────────────────────────────────

const patientAnnie1: CandidatePatient = {
  hash: 'p1',
  linked_patient_hash: null,
  first_name: 'Annie',
  last_name: 'Ainsworth',
  gender: 'Female',
  date_of_birth: '1998-08-16',
  postal_code: 'SW1A 1AA',
  keys: { fullname_dob: null, fullname_postal: null, dob_postal: null },
  id_verified: true,
  id_document_provided: 'Passport',
  idu_status: null,
  gp_name: 'Dr. Smith, St. Mary Medical Centre',
  nhs_number: '123 456 7890',
  mobile: '07700 900123',
  email: 'annie.ainsworth@example.com',
  summary_care_record: 'No significant findings',
  summary_care_record_updated_at: '2025-11-12 09:15:00',
  safeguarding_questions: 'None reported',
  address: {
    street: '10 Downing Street',
    city: 'London',
    postal_code: 'SW1A 1AA',
    county: 'Greater London',
    country: 'United Kingdom',
    address_note: null
  },
  user: { hash: 'u1', display_name: 'Annie Ainsworth', email: 'amie.re@evaro.com', phone: 'E1 607886555333' },
  red_flag: false,
  red_flag_reason: null,
  medications: 'None',
  allergies: "Penicillin- I'm very allergy and cannot have ANY medication that contains it",
  family_history: 'Heart disease, leukaemia',
  social_history: 'None',
  conditions: 'None',
  other: 'None'
}

const patientAnn2: CandidatePatient = {
  hash: 'p2',
  linked_patient_hash: null,
  first_name: 'Ann',
  last_name: 'Ainsworth',
  gender: 'Female',
  date_of_birth: '1998-08-16',
  postal_code: 'E1 6AN',
  keys: { fullname_dob: null, fullname_postal: null, dob_postal: null },
  id_verified: true,
  id_document_provided: 'Driving Licence',
  idu_status: null,
  gp_name: 'Dr. Jones, Whitechapel Health Centre',
  nhs_number: '234 567 8901',
  mobile: '+447700900999',
  email: 'ann.ainsworth@example.com',
  summary_care_record: 'Hypertension under review',
  summary_care_record_updated_at: '2025-12-03 14:42:00',
  safeguarding_questions: 'None reported',
  address: {
    street: '42 Brick Lane',
    city: 'London',
    postal_code: 'E1 6AN',
    county: 'Greater London',
    country: 'United Kingdom',
    address_note: null
  },
  user: { hash: 'u2', display_name: 'Ann Ainsworth', email: 'ann.ains@evaro.com', phone: 'E1 607886555444' },
  red_flag: true,
  red_flag_reason: 'sample reason 1',
  medications: 'High blood pressure 12mg',
  allergies: 'None',
  family_history: 'Leukemia',
  social_history: 'None',
  conditions: 'None',
  other: 'None'
}

const patientAnnie3: CandidatePatient = {
  hash: 'p3',
  linked_patient_hash: null,
  first_name: 'Annie',
  last_name: 'Ainsworth',
  gender: 'Female',
  date_of_birth: '1998-08-16',
  postal_code: 'SW1A 1AA',
  keys: { fullname_dob: null, fullname_postal: null, dob_postal: null },
  id_verified: true,
  id_document_provided: 'Passport',
  idu_status: null,
  gp_name: 'Dr. Patel, Westminster Surgery',
  nhs_number: '345 678 9012',
  mobile: null,
  email: 'annie.ainsworth@example.com',
  summary_care_record: 'No significant findings',
  summary_care_record_updated_at: '2026-01-05 11:30:00',
  safeguarding_questions: 'None reported',
  address: {
    street: '15 Victoria Street',
    city: 'London',
    postal_code: 'SW1A 1AA',
    county: 'Greater London',
    country: 'United Kingdom',
    address_note: null
  },
  user: { hash: 'u3', display_name: 'Annie Ainsworth', email: 'annie.a@evaro.com', phone: 'E1 607886555555' },
  red_flag: true,
  red_flag_reason: 'sample reason 2',
  medications: 'None',
  allergies: 'None',
  family_history: 'None',
  social_history: 'None',
  conditions: 'None',
  other: 'None'
}

// ─── Merge candidates ──────────────────────────────────────────────────────

export const mockMergeCandidates: DuplicateCandidate[] = [
  {
    duplicate_id: 1,
    status: 'pending',
    same_user: true,
    created_at: '2026-01-09 14:30:00',
    patient_a: patientAnnie1,
    patient_b: patientAnn2
  },
  {
    duplicate_id: 2,
    status: 'pending',
    same_user: true,
    created_at: '2026-01-08 10:15:00',
    patient_a: patientAnnie1,
    patient_b: patientAnnie3
  }
]

// ─── Link candidates ───────────────────────────────────────────────────────

export const mockLinkCandidates: DuplicateCandidate[] = [
  {
    duplicate_id: 3,
    status: 'pending',
    same_user: false,
    created_at: '2026-01-09 14:30:00',
    patient_a: patientAnnie1,
    patient_b: patientAnn2
  },
  {
    duplicate_id: 4,
    status: 'pending',
    same_user: false,
    created_at: '2026-01-08 10:15:00',
    patient_a: patientAnnie1,
    patient_b: patientAnnie3
  }
]

export const mockDeferredRecords: DeferredRecord[] = [
  {
    id: 'd1',
    patientRecord: 'Anne Ainsworth',
    numberOfRecords: 2,
    suggestedAction: 'merge',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '09-01-2026 14:30'
  },
  {
    id: 'd2',
    patientRecord: 'Sam Henly',
    numberOfRecords: 3,
    suggestedAction: 'link',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '09-01-2026 14:30'
  },
  {
    id: 'd3',
    patientRecord: 'Louise Carter',
    numberOfRecords: 4,
    suggestedAction: 'merge',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '09-01-2026 14:30'
  },
  {
    id: 'd4',
    patientRecord: 'Rebecca May',
    numberOfRecords: 2,
    suggestedAction: 'merge',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '09-01-2026 14:30'
  },
  {
    id: 'd5',
    patientRecord: 'Liam Charmers',
    numberOfRecords: 2,
    suggestedAction: 'link',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '09-01-2026 14:30'
  },
  {
    id: 'd6',
    patientRecord: 'Thabo Ngwenya',
    numberOfRecords: 3,
    suggestedAction: 'merge',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '09-01-2026 14:30'
  },
  {
    id: 'd7',
    patientRecord: 'Chris Lee',
    numberOfRecords: 2,
    suggestedAction: 'link',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '09-01-2026 14:30'
  },
  {
    id: 'd8',
    patientRecord: 'Gary Freeedman',
    numberOfRecords: 3,
    suggestedAction: 'merge',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '09-01-2026 14:30'
  },
  {
    id: 'd9',
    patientRecord: 'Sarah Johnson',
    numberOfRecords: 2,
    suggestedAction: 'merge',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '08-01-2026 10:15'
  },
  {
    id: 'd10',
    patientRecord: 'James Smith',
    numberOfRecords: 2,
    suggestedAction: 'link',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '08-01-2026 09:45'
  },
  {
    id: 'd11',
    patientRecord: 'Emily Brown',
    numberOfRecords: 3,
    suggestedAction: 'merge',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '07-01-2026 16:20'
  },
  {
    id: 'd12',
    patientRecord: 'Michael Davis',
    numberOfRecords: 2,
    suggestedAction: 'link',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '07-01-2026 11:00'
  },
  {
    id: 'd13',
    patientRecord: 'Lisa Wilson',
    numberOfRecords: 4,
    suggestedAction: 'merge',
    deferredBy: 'CS team member name',
    deferredByInitials: 'KJ',
    dateDeferred: '06-01-2026 14:30'
  }
]

export const mockHistoryRecords: HistoryRecord[] = [
  {
    id: 'h1',
    patientRecord: 'Anne Ainsworth',
    numberOfRecords: 2,
    action: 'merged',
    by: 'CS team member name',
    byInitials: 'KJ',
    date: '09-01-2026 14:30'
  },
  {
    id: 'h2',
    patientRecord: 'Sam Henly',
    numberOfRecords: 3,
    action: 'linked',
    by: 'CS team member name',
    byInitials: 'KJ',
    date: '09-01-2026 14:30'
  },
  {
    id: 'h3',
    patientRecord: 'Louise Carter',
    numberOfRecords: 4,
    action: 'merged',
    by: 'CS team member name',
    byInitials: 'KJ',
    date: '09-01-2026 14:30'
  },
  {
    id: 'h4',
    patientRecord: 'Rebecca May',
    numberOfRecords: 2,
    action: 'linked',
    by: 'CS team member name',
    byInitials: 'KJ',
    date: '09-01-2026 14:30'
  },
  {
    id: 'h5',
    patientRecord: 'Liam Charmers',
    numberOfRecords: 2,
    action: 'linked',
    by: 'CS team member name',
    byInitials: 'KJ',
    date: '09-01-2026 14:30'
  },
  {
    id: 'h6',
    patientRecord: 'Thabo Ngwenya',
    numberOfRecords: 3,
    action: 'merged',
    by: 'CS team member name',
    byInitials: 'KJ',
    date: '09-01-2026 14:30'
  },
  {
    id: 'h7',
    patientRecord: 'Samuel Simsphiwe',
    numberOfRecords: 2,
    action: 'linked',
    by: 'CS team member name',
    byInitials: 'KJ',
    date: '09-01-2026 14:30'
  },
  {
    id: 'h8',
    patientRecord: 'Anne Ainsworth',
    numberOfRecords: 3,
    action: 'merged',
    by: 'CS team member name',
    byInitials: 'KJ',
    date: '09-01-2026 14:30'
  },
  {
    id: 'h9',
    patientRecord: 'David Thompson',
    numberOfRecords: 2,
    action: 'merged',
    by: 'CS team member name',
    byInitials: 'KJ',
    date: '08-01-2026 11:00'
  },
  {
    id: 'h10',
    patientRecord: 'Karen White',
    numberOfRecords: 2,
    action: 'linked',
    by: 'CS team member name',
    byInitials: 'KJ',
    date: '08-01-2026 09:30'
  },
  {
    id: 'h11',
    patientRecord: 'Robert Green',
    numberOfRecords: 3,
    action: 'merged',
    by: 'CS team member name',
    byInitials: 'KJ',
    date: '07-01-2026 15:45'
  },
  {
    id: 'h12',
    patientRecord: 'Jane Taylor',
    numberOfRecords: 2,
    action: 'linked',
    by: 'CS team member name',
    byInitials: 'KJ',
    date: '07-01-2026 10:20'
  }
]

export const mockDeduplicationData: DeduplicationData = {
  mergePatients: mockMergeCandidates,
  linkPatients: mockLinkCandidates,
  deferredRequests: mockDeferredRecords,
  history: mockHistoryRecords
}
